import { useEffect, useState } from "react";
import { Gem, Search as SearchIcon, TrendingUp, Users, Building2, CreditCard, Crown, HeartHandshake, Trash2, Plus, Loader2, AlertTriangle, Sparkles, Brain, Activity, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, Scroller, Spinner } from "@/pages/_shared";

function CleanupPanel({ onDone }) {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const dryRun = async () => {
    setBusy(true);
    try { const { data } = await api.post("/platform/cleanup?confirm=false"); setReport(data); }
    catch (e) { toast.error("Could not run audit"); } finally { setBusy(false); }
  };
  const confirmDelete = async () => {
    if (!window.confirm("Permanently remove the identified test/demo records? Genuine data is preserved.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/platform/cleanup?confirm=true");
      toast.success("Test data removed and metrics recalculated");
      setReport({ ...data, done: true });
      onDone?.();
    } catch (e) { toast.error("Cleanup failed"); } finally { setBusy(false); }
  };
  const rows = report ? Object.entries(report.would_remove || report.removed || {}) : [];
  const total = rows.reduce((n, [, v]) => n + (Number(v) || 0), 0);
  const cat = report?.categorisation?.organisations;
  const catBlocks = cat ? [
    ["Genuine SaaS customers (preserved)", cat.genuine_saas_customer, "emerald"],
    ["BDVG / internal (preserved)", cat.bdvg_internal, "gold"],
    ["Complimentary — Founder/Family (preserved)", cat.complimentary_founder_family, "sky"],
    ["Test — will delete", cat.test_will_delete, "red"],
    ["Demo / seed — will delete", cat.demo_seed_will_delete, "red"],
    ["Ambiguous — left untouched", cat.ambiguous_left_untouched, "amber"],
  ] : [];
  const tone = { emerald: "border-emerald-400/25 text-emerald-200", gold: "border-gold/25 text-gold",
    sky: "border-sky-400/25 text-sky-200", red: "border-red-400/25 text-red-200", amber: "border-amber-400/25 text-amber-200" };
  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-charcoal p-5" data-testid="cleanup-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5"><Sparkles className="h-5 w-5 text-gold" /><span className="font-heading font-semibold">Production data cleanup</span></div>
        <button onClick={dryRun} disabled={busy} className="rounded-xl border border-white/10 px-3.5 py-2 text-sm text-white/75 hover:border-gold/30" data-testid="cleanup-dryrun">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run audit (dry-run)"}
        </button>
      </div>
      <p className="mt-2 text-xs text-white/40">Forensically categorises every organisation &amp; user, then deletes only records positively identified as test/demo/seed (test owner-emails, TEST_/DEMO_ names, demo orgs, confirmed seed accounts). Never touches Founder, BDVG, platform owner, real customers or live Stripe. Ambiguous records are reported, never deleted. Nothing is deleted until you confirm.</p>
      {report && (
        <div className="mt-4">
          {catBlocks.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="cleanup-categorisation">
              {catBlocks.map(([label, items, c]) => (
                <div key={label} className={`rounded-lg border bg-graphite/40 px-3 py-2 ${tone[c]}`}>
                  <p className="text-[11px] font-semibold uppercase tracking-wider">{label} — {items?.length || 0}</p>
                  {items?.length > 0 && (
                    <p className="mt-1 text-[11px] text-white/55">{items.map((o) => `${o.name}${o.owner_email ? ` (${o.owner_email})` : ""}`).join(" · ")}</p>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="mb-2 text-xs uppercase tracking-wider text-white/40">{report.done ? "Removed" : "Would remove"} — {total} record(s)</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {rows.map(([k, v]) => (
              <div key={k} className="rounded-lg border border-white/8 bg-graphite px-3 py-2" data-testid={`cleanup-count-${k}`}>
                <span className="font-heading text-lg font-bold text-white/85">{v}</span>
                <span className="ml-2 text-[11px] text-white/45">{k.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
          {report.summary_table && (
            <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg border border-emerald-400/20 bg-emerald-500/[0.04] p-3 text-xs sm:grid-cols-4" data-testid="cleanup-summary">
              {Object.entries(report.summary_table).map(([k, v]) => (
                <div key={k}><span className="font-heading text-base font-bold text-white/85">{v}</span><span className="ml-1.5 text-[10px] text-white/45">{k.replace(/_/g, " ")}</span></div>
              ))}
            </div>
          )}
          {report.ambiguous_left_untouched?.length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-400/25 bg-amber-500/[0.05] p-3 text-xs text-amber-200" data-testid="cleanup-ambiguous">
              <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />{report.ambiguous_left_untouched.length} ambiguous record(s) left untouched for your review: {report.ambiguous_left_untouched.map((a) => a.name).join(", ")}
            </div>
          )}
          {!report.done && total > 0 && (
            <button onClick={confirmDelete} disabled={busy} className="mt-4 flex items-center gap-2 rounded-xl bg-red-500/90 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-40" data-testid="cleanup-confirm">
              <Trash2 className="h-4 w-4" /> Confirm delete {total} record(s)
            </button>
          )}
          {report.done && <p className="mt-3 text-sm text-emerald-300">✓ Cleanup complete. Metrics recalculated from genuine records.</p>}
        </div>
      )}
    </div>
  );
}

function SignupsTrend({ metrics }) {
  const trend = metrics?.signups_trend || [];
  const total = metrics?.signups_30d ?? 0;
  const max = Math.max(1, ...trend.map((d) => d.count));
  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-charcoal p-5" data-testid="signups-trend">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5"><TrendingUp className="h-5 w-5 text-gold" /><span className="font-heading font-semibold">Genuine customer signups</span></div>
        <span className="text-sm text-white/55">last 30 days · <strong className="text-gold" data-testid="signups-30d-total">{total}</strong></span>
      </div>
      <p className="mt-1 text-xs text-white/40">New EXTERNAL BDVG AI SaaS customer organisations per day. Internal, BDVG and complimentary accounts are excluded.</p>
      <div className="mt-4 flex h-24 items-end gap-[3px]" data-testid="signups-bars">
        {trend.map((d, i) => (
          <div key={i} className="group relative flex-1" title={`${d.date}: ${d.count}`}>
            <div
              className="w-full rounded-t-sm bg-gradient-to-t from-gold/30 to-gold transition-all hover:from-gold/50 hover:to-gold"
              style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-white/30">
        <span>{trend[0]?.date?.slice(5)}</span>
        <span>today</span>
      </div>
      {total === 0 && <p className="mt-2 text-xs text-white/35">No genuine customer signups in the last 30 days yet.</p>}
    </div>
  );
}

function IntelligenceHealth() {
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/platform/intelligence").then(({ data }) => setH(data)).catch(() => {}); }, []);
  if (!h) return null;
  const rows = [
    ["Memory system", h.memory_system.status, `${h.memory_system.records} records`],
    ["Learning pipeline", h.learning_pipeline.status, `${h.learning_pipeline.profiles} profiles · ${h.learning_pipeline.active_facts} facts · ${h.learning_pipeline.signals_7d} signals/7d`],
    ["Recommendation engine", h.recommendation_engine.status, `${h.recommendation_engine.sets} sets · ${h.recommendation_engine.outcomes} outcomes`],
    ["Collective intelligence", h.collective_intelligence.status, `${h.collective_intelligence.patterns} anonymised patterns`],
    ["Automation", h.automation.status, `${h.automation.failures_7d} failures/7d`],
  ];
  const attn = (h.attention?.email_failures || 0) + (h.attention?.automation_failures_7d || 0);
  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-charcoal p-5" data-testid="intelligence-health">
      <div className="flex items-center gap-2.5"><Brain className="h-5 w-5 text-gold" /><span className="font-heading font-semibold">Intelligence health</span></div>
      <p className="mt-1 text-xs text-white/40">High-level system status only — private user memory content is never shown here.</p>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {rows.map(([label, status, detail]) => (
          <div key={label} className="flex items-center justify-between rounded-xl border border-white/8 bg-graphite px-4 py-3" data-testid={`health-${label.toLowerCase().replace(/ /g, "-")}`}>
            <div>
              <p className="text-sm text-white/85">{label}</p>
              <p className="text-[11px] text-white/40">{detail}</p>
            </div>
            <span className={`flex items-center gap-1.5 text-xs ${status === "operational" ? "text-emerald-300" : "text-white/40"}`}>
              {status === "operational" ? <CheckCircle2 className="h-4 w-4" /> : <Activity className="h-4 w-4" />}{status}
            </span>
          </div>
        ))}
      </div>
      <div className={`mt-3 rounded-xl border px-4 py-2.5 text-xs ${attn ? "border-amber-400/25 bg-amber-500/[0.05] text-amber-200" : "border-emerald-400/20 bg-emerald-500/[0.04] text-emerald-200"}`} data-testid="health-attention">
        {attn ? `${attn} item(s) need attention — ${h.attention.email_failures} email failure(s), ${h.attention.automation_failures_7d} automation failure(s) in 7d.` : "No failures requiring attention."}
      </div>
    </div>
  );
}

function FounderFamilyPanel() {  const [rows, setRows] = useState(null);
  const [email, setEmail] = useState("");
  const [compType, setCompType] = useState("family");
  const [minor, setMinor] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/platform/comp-access").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const grant = async () => {
    if (!email.trim()) return toast.error("Enter the member's email");
    setBusy(true);
    try {
      const { data } = await api.post("/platform/comp-access", { email: email.trim(), comp_type: compType, minor });
      toast.success(data.pending ? `Invitation pending for ${data.email} — access attaches when they register` : `${data.label} granted to ${data.org_name}`);
      setEmail(""); setMinor(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not grant access"); }
    finally { setBusy(false); }
  };
  const resend = async (row) => {
    try { const { data } = await api.post(`/platform/comp-invites/${row.invite_id}/resend`); toast.success(data.email_delivery === "sent" ? `Invitation email sent to ${data.email}` : `Invite refreshed for ${data.email} (email ${data.email_delivery}${data.email_error ? ": " + data.email_error : ""})`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
  };
  const revoke = async (row) => {
    if (!window.confirm(`Revoke ${row.status === "pending" ? "invitation" : "access"} for ${row.owner_email || row.org_name}?`)) return;
    try {
      if (row.status === "pending") await api.delete(`/platform/comp-invites/${row.invite_id}`);
      else await api.delete(`/platform/comp-access/${row.org_id}`);
      toast.success("Revoked"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not revoke"); }
  };

  return (
    <div className="mt-8 rounded-2xl border border-gold/20 bg-gradient-to-br from-gold/[0.04] to-transparent p-6" data-testid="founder-family-panel">
      <div className="flex items-center gap-2"><Crown className="h-5 w-5 text-gold" /><h3 className="font-heading text-lg font-semibold">Founder &amp; Family Access</h3></div>
      <p className="mt-1 text-sm text-white/45">Grant complimentary, permanent BDVG AI access. No Stripe subscription is created. Family members marked as under-18 have purchases &amp; billing disabled.</p>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs text-white/40">Member account email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@example.com" data-testid="comp-email-input"
            className="mt-1 w-full rounded-xl border border-white/10 bg-graphite px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/40 focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-white/40">Entitlement</label>
          <select value={compType} onChange={(e) => setCompType(e.target.value)} data-testid="comp-type-select"
            className="mt-1 rounded-xl border border-white/10 bg-graphite px-3 py-2.5 text-sm text-white focus:border-gold/40 focus:outline-none">
            <option value="family">Family — Complimentary</option>
            <option value="founder">Founder — Permanent</option>
          </select>
        </div>
        <label className="flex items-center gap-2 pb-2.5 text-sm text-white/70">
          <input type="checkbox" checked={minor} onChange={(e) => setMinor(e.target.checked)} data-testid="comp-minor-checkbox" className="accent-gold" /> Under 18 (guardian-managed)
        </label>
        <button onClick={grant} disabled={busy} className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-gold-light to-gold px-4 py-2.5 text-sm font-semibold text-black disabled:opacity-40" data-testid="comp-grant-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Grant access
        </button>
      </div>

      <div className="mt-5 overflow-hidden rounded-xl border border-white/8">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/8 bg-charcoal text-left text-xs uppercase tracking-wider text-white/40">
            <th className="px-4 py-3">Member</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Granted by</th><th className="px-4 py-3"></th></tr></thead>
          <tbody data-testid="comp-access-list">
            {rows == null ? (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-white/30"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-white/30">No complimentary accounts yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.org_id || r.invite_id} className="border-b border-white/5" data-testid="comp-access-row">
                <td className="px-4 py-3 text-white/80">{r.owner_name || r.owner_email}<div className="text-xs text-white/40">{r.owner_email}</div></td>
                <td className="px-4 py-3">
                  {(() => {
                    const map = {
                      founder: "bg-gold/20 text-gold",
                      family_active: "bg-emerald-500/15 text-emerald-300",
                      pending: "bg-amber-500/15 text-amber-300",
                      expired: "bg-white/10 text-white/50",
                      revoked: "bg-red-500/15 text-red-300",
                    };
                    const key = r.status === "active" ? (r.comp_type === "founder" ? "founder" : "family_active") : r.status;
                    const label = r.status_label || (r.status === "active" ? r.label : r.status);
                    return (
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${map[key] || "bg-white/10 text-white/60"}`} data-testid={`comp-status-${r.org_id || r.invite_id}`}>
                        {key === "founder" ? <Crown className="h-3 w-3" /> : key === "family_active" ? <HeartHandshake className="h-3 w-3" /> : null}
                        {label}{r.minor ? " · Minor" : ""}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 text-white/50">{r.granted_by || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-3">
                    {(r.status === "pending" || r.status === "expired") && (
                      <button onClick={() => resend(r)} className="text-xs text-gold hover:underline" data-testid={`comp-resend-${r.invite_id}`}>{r.status === "expired" ? "Resend (expired)" : "Resend"}</button>
                    )}
                    {r.status !== "revoked" && (
                      <button onClick={() => revoke(r)} className="text-white/40 hover:text-destructive" data-testid={`comp-revoke-${r.org_id || r.invite_id}`}><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PlatformConsole() {
  const [metrics, setMetrics] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");

  const loadOrgs = (query) => api.get("/platform/orgs", { params: query ? { q: query } : {} }).then(({ data }) => setOrgs(data)).catch(() => {});
  const reloadMetrics = () => api.get("/platform/metrics").then(({ data }) => setMetrics(data)).catch(() => {});
  useEffect(() => {
    reloadMetrics();
    loadOrgs("");
  }, []);

  if (!metrics) return <Spinner />;
  const org = metrics.saas_customer_orgs ?? metrics.organisations ?? metrics.total_organisations ?? 0;
  const cards = [
    { label: "SaaS Customers", value: org, icon: Building2 },
    { label: "Paid", value: metrics.paid, icon: CreditCard },
    { label: "Trials", value: metrics.trials, icon: Users },
    { label: "MRR", value: `£${metrics.mrr}`, icon: TrendingUp },
    { label: "ARR", value: `£${metrics.arr}`, icon: TrendingUp },
    { label: "Active Users", value: metrics.active_users, icon: Users },
  ];

  return (
    <Scroller>
      <PageHeader title="Platform Owner Console" subtitle="BDVG AI SaaS — business metrics & customer accounts" icon={Gem} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-gold/15 bg-gradient-to-br from-gold/[0.05] to-transparent p-5" data-testid={`platform-stat-${c.label.toLowerCase().replace(/ /g,'-')}`}>
            <c.icon className="h-5 w-5 text-gold" />
            <p className="mt-3 font-heading text-3xl font-bold">{c.value}</p>
            <p className="text-sm text-white/45">{c.label}</p>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-white/35">
        <strong className="text-white/55">SaaS Customers</strong> counts only genuine EXTERNAL BDVG AI customer organisations. Paid, Trials, MRR, ARR &amp; Active Users are derived from those customers only — your own internal companies and complimentary access are excluded and shown separately below.
      </p>
      <div className="mt-3 flex flex-wrap gap-2 text-xs" data-testid="platform-breakdown">
        <span className="rounded-lg border border-gold/20 bg-gold/[0.05] px-3 py-1.5 text-white/70" data-testid="breakdown-internal">
          BDVG / internal organisations: <strong className="text-gold">{metrics.bdvg_internal_orgs ?? 0}</strong>
        </span>
        <span className="rounded-lg border border-white/10 bg-graphite px-3 py-1.5 text-white/70" data-testid="breakdown-complimentary">
          Complimentary (Founder / Family): <strong className="text-white/85">{metrics.complimentary_orgs ?? metrics.complimentary ?? 0}</strong>
        </span>
        <span className="rounded-lg border border-white/10 bg-graphite px-3 py-1.5 text-white/70" data-testid="breakdown-internal-users">
          Internal / complimentary users: <strong className="text-white/85">{metrics.internal_users ?? 0}</strong>
        </span>
        <span className="rounded-lg border border-white/10 bg-graphite px-3 py-1.5 text-white/55" data-testid="breakdown-total">
          Total organisations (all types): <strong className="text-white/75">{metrics.total_all_orgs ?? 0}</strong>
        </span>
      </div>

      <CleanupPanel onDone={reloadMetrics} />

      <SignupsTrend metrics={metrics} />

      <IntelligenceHealth />

      <div className="mt-8 mb-3 flex items-center gap-3 rounded-xl border border-white/10 bg-graphite px-4 py-2.5">
        <SearchIcon className="h-4 w-4 text-white/30" />
        <input value={q} onChange={(e) => { setQ(e.target.value); loadOrgs(e.target.value); }} placeholder="Search customer organisations…" data-testid="platform-org-search"
          className="w-full bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none" />
      </div>
      <div className="overflow-hidden rounded-2xl border border-white/8">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/8 bg-charcoal text-left text-xs uppercase tracking-wider text-white/40">
            <th className="px-4 py-3">Organisation</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Industry</th><th className="px-4 py-3">Users</th></tr></thead>
          <tbody>
            {orgs.map((o) => (
              <tr key={o.id} className="border-b border-white/5" data-testid="platform-org-row">
                <td className="px-4 py-3 text-white/80">{o.name}{o.is_bdvg && <span className="ml-2 rounded bg-gold/15 px-1.5 py-0.5 text-[10px] text-gold">BDVG</span>}{o.is_demo && <span className="ml-2 rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-white/50">DEMO</span>}</td>
                <td className="px-4 py-3 capitalize text-white/60">{o.subscription_status}</td>
                <td className="px-4 py-3 capitalize text-white/60">{o.industry || "—"}</td>
                <td className="px-4 py-3 text-white/60">{o.users}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-white/30">Support access to any customer environment is permission-gated and written to the audit log. Confidential business data is never exposed on this console.</p>

      <FounderFamilyPanel />
    </Scroller>
  );
}
